import { notify } from "@general-backoffice/core/index";
import i18n from "i18next";
import { useEffect, useRef, useState } from "react";

import {
  type ExchangeAccount,
  type ExchangeAccountCreateInput,
  type ExchangeAccountCredentials,
  exchangeAccountsEndpoints,
  type ExchangeAccountUpdateInput,
} from "../../../api";
import { type StrapiId, useStrapiOne } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import { hasDataChanged } from "../../../utils/hasDataChanged";
import { getFromCache, setToCache } from "../../../utils/memoryCache";
import handleError from "../../utils/handleError";

interface UseExchangeAccountProps {
  [key: string]: unknown;
}

const useExchangeAccount = (id?: StrapiId, props?: UseExchangeAccountProps) => {
  const cacheKey = id ? `exchange_account_${id}` : null;
  const cached = cacheKey ? getFromCache<ExchangeAccount>(cacheKey) : null;
  const [cachedItem, setCachedItem] = useState<ExchangeAccount | null>(cached?.data || null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasInitialCache = useRef(!!cached);

  // Re-seed when the cache key changes. The initial state is evaluated once
  // per mount, so navigating between two entities served by the same route
  // component changed the key while keeping the component instance, and the
  // hook kept returning the previous entity's data as if it were current.
  const seededCacheKeyRef = useRef(cacheKey);
  if (seededCacheKeyRef.current !== cacheKey) {
    seededCacheKeyRef.current = cacheKey;
    const nextCached = cacheKey ? getFromCache<ExchangeAccount>(cacheKey) : null;
    setCachedItem(nextCached?.data || null);
    hasInitialCache.current = !!nextCached;
  }
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const {
    item: exchangeAccount,
    setItem: setExchangeAccount,
    isLoading,
    setLoading,
    ...other
  } = useStrapiOne<ExchangeAccount>({
    id,
    ...props,
    loadItem: loadExchangeAccount,
    submitItem: submitExchangeAccount,
  });

  // Update cache when fresh data arrives (only if changed)
  useEffect(() => {
    if (!mountedRef.current) return;
    if (!isLoading && exchangeAccount && cacheKey) {
      // Only update if data actually changed
      if (hasDataChanged(cachedItem, exchangeAccount)) {
        setToCache(cacheKey, exchangeAccount);
        setCachedItem(exchangeAccount);
      }
      setIsRefreshing(false);
    }
  }, [exchangeAccount, isLoading, cacheKey, cachedItem]);

  // Track refreshing state
  useEffect(() => {
    if (!mountedRef.current) return;
    if (isLoading && hasInitialCache.current) {
      setIsRefreshing(true);
    }
  }, [isLoading]);

  // Use cached data if available while loading
  const item = cacheKey && cachedItem ? cachedItem : exchangeAccount;
  const effectiveLoading = cacheKey ? !hasInitialCache.current && isLoading : isLoading;

  return {
    exchangeAccount: item,
    setExchangeAccount,
    isLoading: effectiveLoading,
    isRefreshing,
    setLoading,
    ...other,
  };
};

export const loadExchangeAccount = async (
  id: StrapiId,
  params?: Record<string, unknown>
): Promise<ExchangeAccount | undefined> => {
  try {
    const numericId = typeof id === "string" ? parseInt(id, 10) : id;
    const { data } = await exchangeAccountsEndpoints.getExchangeAccount(numericId, params);
    return data;
  } catch (e: unknown) {
    handleError(getErrorMessage(e, "Error while loading exchange account"));
  }
};

// Form data extends entity with credentials (not returned from API for security)
type ExchangeAccountFormData = ExchangeAccount & { credentials?: ExchangeAccountCredentials };

export const submitExchangeAccount = async (
  exchangeAccount: ExchangeAccountFormData
): Promise<ExchangeAccount | undefined> => {
  const isNew = !exchangeAccount?.id;
  const errMsg = `Exchange account ${isNew ? "creation" : "update"} failed`;
  if (!exchangeAccount) throw new Error(errMsg);

  try {
    const { id, name, exchange, credentials, defaultLeverage } = exchangeAccount;

    if (isNew) {
      // Extract exchange ID from either populated object or direct ID
      const exchangeId = typeof exchange === "object" && exchange !== null && "id" in exchange ? exchange.id : exchange;

      if (!exchangeId) {
        throw new Error("Exchange is required for new exchange account");
      }

      // Strip null values from credentials — Zod schema expects string|undefined, not null
      const cleanCredentials = credentials
        ? {
            username: credentials.username ?? undefined,
            password: credentials.password ?? undefined,
            server: credentials.server ?? undefined,
            serverConfirmed: credentials.serverConfirmed ?? undefined,
          }
        : undefined;

      const createBody: ExchangeAccountCreateInput = {
        name: name || `Account ${Date.now()}`,
        exchange: Number(exchangeId),
        credentials: cleanCredentials,
        defaultLeverage: defaultLeverage ?? undefined,
      };

      const { data } = await exchangeAccountsEndpoints.createExchangeAccount(createBody);
      notify.success("Exchange account created successfully");
      return data;
    } else {
      const numericId = typeof id === "number" ? id : parseInt(String(id), 10);
      const updateBody: ExchangeAccountUpdateInput = {
        name: name ?? undefined,
        credentials: credentials ?? undefined,
        defaultLeverage: defaultLeverage ?? undefined,
      };

      const { data } = await exchangeAccountsEndpoints.updateExchangeAccount(numericId, updateBody);
      notify.success("Exchange account updated successfully");
      return data;
    }
  } catch (e: unknown) {
    // Try to parse structured duplicate error from API
    const errorMessage = getErrorMessage(e, errMsg);

    try {
      const errorData = JSON.parse(errorMessage);

      if (errorData.code === "DUPLICATE_OWN") {
        // User's own duplicate credentials - show message with link to existing account
        const localizedMessage = i18n.t("exchangeAccounts:errors.duplicateOwn");
        const linkText = i18n.t("exchangeAccounts:errors.duplicateOwnLink");

        if (errorData.exchangeAccountId) {
          // Show toast with link to existing account
          notify.error(
            <span>
              {localizedMessage}{" "}
              <a
                href={`/exchange-accounts/${errorData.exchangeAccountId}`}
                style={{ color: "white", textDecoration: "underline", fontWeight: "bold" }}
              >
                {linkText}
              </a>
            </span>,
            { autoClose: 10000 }
          );
        } else {
          handleError(localizedMessage);
        }
        throw new Error(localizedMessage);
      } else if (errorData.code === "INVALID_SERVER") {
        const localizedMessage = i18n.t("exchangeAccounts:errors.invalidServer");
        handleError(localizedMessage);
        throw new Error(localizedMessage);
      } else if (errorData.code === "DUPLICATE_OTHER") {
        // Another user's credentials - show localized message
        const localizedMessage = i18n.t("exchangeAccounts:errors.duplicateOther");
        handleError(localizedMessage);
        throw new Error(localizedMessage);
      }
    } catch (parseError) {
      // Not a JSON error, check if it's our error being re-thrown
      if (!(parseError instanceof SyntaxError)) {
        throw parseError;
      }
    }

    handleError(errorMessage);
  }
};

export default useExchangeAccount;
